<template>
	<Teleport to="body">
		<div
			v-for="popup in popups"
			:key="popup.id"
			class="popup"
			:class="{ popup_show: popup.visible }"
			:aria-hidden="!popup.visible"
			:inert="!popup.visible"
			@click.self="closePopup(popup.id)"
		>
			<div class="popup__wrapper">
				<div class="popup__content">
					<button
						data-close
						type="button"
						class="popup__close"
						@click="closePopup(popup.id)"
					>
						<svg width="48" height="48">
							<use :href="`#close`" />
						</svg>
					</button>

					<div class="popup__block">
						<component :is="popup.component" v-bind="popup.props" />
					</div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<script setup>
import { popupRegistry } from '@/components/popup/popupRegistry'
import { onMounted, onUnmounted, ref, nextTick, markRaw } from 'vue'
import { bodyLock, bodyUnlock } from '@/utils/lockScroll'

const popups = ref([])

function openPopup(id, component = null, props = {}) {
	const existing = popups.value.find((p) => p.id === id)

	if (!existing) {
		const loadComponent = component
			? Promise.resolve({ default: component })
			: popupRegistry[id]
				? popupRegistry[id]()
				: Promise.reject(`Компонент с id "${id}" не найден в popupRegistry`)

		loadComponent
			.then(({ default: Comp }) => {
				popups.value.push({
					id,
					component: markRaw(Comp),
					props,
					visible: false,
					closingTimeout: null,
				})

				nextTick(() => {
					requestAnimationFrame(() => {
						const popup = popups.value.find((p) => p.id === id)
						if (popup) {
							popup.visible = true
							updateBodyLock()
						}
					})
				})
			})
			.catch((err) => {
				console.warn(`Ошибка загрузки попапа "${id}":`, err)
			})
	} else {
		if (existing.closingTimeout) {
			clearTimeout(existing.closingTimeout)
			existing.closingTimeout = null
		}
		if (!existing.visible) {
			existing.visible = true
			updateBodyLock()
		}
	}
}

function closePopup(id) {
	const popup = popups.value.find((p) => p.id === id)
	if (popup && popup.visible) {
		nextTick(() => {
			requestAnimationFrame(() => {
				const popupElement =
					document.querySelector(`.popup[key="${id}"]`) ||
					document.querySelector(`.popup.popup_show`)
				if (popupElement && popupElement.contains(document.activeElement)) {
					document.activeElement.blur()
				}
			})
		})

		popup.visible = false
		updateBodyLock()

		if (popup.closingTimeout) {
			clearTimeout(popup.closingTimeout)
		}
		popup.closingTimeout = setTimeout(() => {
			const index = popups.value.findIndex((p) => p.id === id)
			if (index !== -1) {
				popups.value.splice(index, 1)
			}
		}, 3000)
	}
}

function closeAllPopups() {
	;[...popups.value].forEach((p) => closePopup(p.id))
}

function updateBodyLock() {
	const anyVisible = popups.value.some((p) => p.visible)
	if (anyVisible) {
		document.documentElement.classList.add('popup-show')
		bodyLock()
	} else {
		document.documentElement.classList.remove('popup-show')
		bodyUnlock()
	}
}

// ============== EVENT LISTENERS ==============

function handleGlobalClick(e) {
	const openBtn = e.target.closest('[data-popup]')
	if (openBtn) {
		e.preventDefault()
		const selector = openBtn.getAttribute('data-popup')
		const id = selector.replace('#', '')
		openPopup(id)
	}
}

function handleEsc(e) {
	if (e.key === 'Escape') {
		closeAllPopups()
	}
}

onMounted(() => {
	document.addEventListener('click', handleGlobalClick)
	document.addEventListener('keydown', handleEsc)
})

onUnmounted(() => {
	document.removeEventListener('click', handleGlobalClick)
	document.removeEventListener('keydown', handleEsc)
})

defineExpose({ openPopup, closePopup, closeAllPopups })
</script>

<style scoped lang="scss">
.popup {
	position: fixed;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	padding: 4.5rem 1.5rem;
	transition: all 0.8s ease 0.1s;
	visibility: hidden;
	pointer-events: none;
	z-index: 1000;
	overflow-y: auto;

	&::after {
		content: '';
		background: rgba(0, 0, 0, 0.6);
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		opacity: 0;
		transition: opacity 0.8s ease 0s;
		z-index: 1;
	}

	&.popup_show {
		visibility: visible;
		pointer-events: auto;

		&::after {
			opacity: 1;
		}

		.popup__content {
			transform: scale(1);
		}
	}
}

.popup__wrapper {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 100%;
	flex: 1 1 auto;
	width: 100%;
}

.popup__close {
	position: absolute;
	right: 1.8rem;
	top: 1.8rem;
	z-index: 10;
	width: 4.8rem;
	height: 4.8rem;

	@media (max-width: 991.98px) {
		top: 1rem;
		right: 1rem;
		width: 2.4rem;
		height: 2.4rem;
	}

	svg,
	img {
		width: 100%;
		height: 100%;
	}

	img {
		object-fit: contain;
	}
}

.popup__content {
	transform: scale(0);
	transition: all 0.3s ease 0s;
	background: #323232;
	border: 1px solid rgba(255, 255, 255, 0.24);
	border-radius: 2rem;
	padding: 6.4rem 4rem 4.4rem;
	width: 100%;
	max-width: 90.5rem;
	position: relative;
	z-index: 10;

	@media (max-width: 991.98px) {
		padding: 3rem 2rem;
	}
}
</style>
